import { useRef, useState } from 'react'
import type {
  FaceData,
  HSACaptureData,
  HsaData,
  HsaDebug,
} from '../index'
import { Phase } from './types'

export type HsaResult = HsaData & {
  debug?: HsaDebug
}

export type HSACaptureState = {
  phase: Phase
  capturedCount: number
  targetCount: number
  progress: number
  result: HsaResult | null
  nativeState: HSACaptureData | null
  start: () => void
  stop: () => void
  clear: () => void
  applyNativeState: (state: HSACaptureData | undefined) => void
  handleFaceData: (incoming: FaceData) => void
}

const mapNativePhase = (state: HSACaptureData): Phase => {
  if (state.phase === 'idle') return 'idle'
  if (state.phase === 'positioning') return 'positioning'
  if (state.phase === 'finding-lens') return 'findingFrame'
  if (state.phase === 'capturing') return 'sampling'
  if (state.phase === 'complete') return 'complete'
  return 'failed'
}

export function useHSACapture(): HSACaptureState {
  const [phase, setPhase] = useState<Phase>('idle')
  const [capturedCount, setCapturedCount] = useState(0)
  const [targetCount, setTargetCount] = useState(1)
  const [progress, setProgress] = useState(0)
  const [result, setResult] = useState<HsaResult | null>(null)
  const [nativeState, setNativeState] = useState<HSACaptureData | null>(null)
  const activeRef = useRef(false)

  const start = () => {
    activeRef.current = true
    setCapturedCount(0)
    setTargetCount(1)
    setProgress(0)
    setResult(null)
    setNativeState(null)
    setPhase('positioning')
  }

  const stop = () => {
    activeRef.current = false
    setPhase('idle')
  }

  const clear = () => {
    activeRef.current = false
    setCapturedCount(0)
    setTargetCount(1)
    setProgress(0)
    setResult(null)
    setNativeState(null)
    setPhase('idle')
  }

  const applyNativeState = (state: HSACaptureData | undefined) => {
    if (!state) return

    activeRef.current = state.active
    setNativeState(state)
    setCapturedCount(state.capturedCount)
    setTargetCount(Math.max(1, state.targetCount))
    setProgress(state.progress)
    setPhase(mapNativePhase(state))
  }

  const handleFaceData = (incoming: FaceData) => {
    if (incoming.hsaCapture) {
      applyNativeState(incoming.hsaCapture)
    }

    if (!activeRef.current) return

    if (incoming.hsa && incoming.hsaCapture?.phase === 'complete') {
      setResult({ ...incoming.hsa, debug: incoming.hsaDebug })
    }
  }

  return {
    phase,
    capturedCount,
    targetCount,
    progress,
    result,
    nativeState,
    start,
    stop,
    clear,
    applyNativeState,
    handleFaceData,
  }
}
